"use client";

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "@/lib/axios";
import { useAuthStore } from "@/stores/authStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { HourlyHeatmap } from "@/components/dashboard/HourlyHeatmap";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  CalendarDays,
  Clock,
  LayoutDashboard,
  PackageMinus,
  PiggyBank,
  Receipt,
  ShoppingBag,
  Sparkles,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { useMe } from "@/hooks/api/useMe";
import { useIsDarkMode } from "@/hooks/useUiTheme";
import { chartTheme, chartTooltipProps } from "@/lib/chart-theme";

const money = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const moneyDecimal = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const SKY_PALETTE = ["#0ea5e9", "#38bdf8", "#7dd3fc", "#0369a1", "#075985", "#bae6fd"];

type Summary = {
  today: {
    sales_amount: number;
    bills_count: number;
    profit: number;
    avg_bill: number;
  };
  this_month: { sales_amount: number; bills_count: number };
  outstanding: { customer_credit: number; supplier_payable: number };
  alerts: {
    low_stock_count: number;
    expiry_soon_count: number;
    pending_pos_count: number;
  };
};

function normalizeMatrix(raw: unknown): { count: number; amount: number }[][] {
  const out: { count: number; amount: number }[][] = [];
  for (let d = 0; d < 7; d++) {
    out[d] = [];
    const rows = Array.isArray(raw) ? (raw as unknown[]) : [];
    const row = Array.isArray(rows[d]) ? (rows[d] as unknown[]) : null;
    for (let h = 0; h < 24; h++) {
      const rawCell = row?.[h];
      const cell =
        rawCell && typeof rawCell === "object"
          ? (rawCell as { count?: number; amount?: number })
          : {};
      out[d][h] = {
        count: Number(cell.count ?? 0),
        amount: Number(cell.amount ?? 0),
      };
    }
  }

  return out;
}

function formatToday(): string {
  return new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function DashboardPage() {
  const [revPeriod, setRevPeriod] = useState<"7d" | "30d" | "90d">("7d");
  const accessToken = useAuthStore((s) => s.accessToken);
  const queryEnabled = Boolean(accessToken);
  const { data: me } = useMe();
  const isDarkTheme = useIsDarkMode();
  const theme = chartTheme(isDarkTheme);
  const tooltipProps = chartTooltipProps(isDarkTheme);

  const summaryQ = useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: async () => {
      const { data } = await api.get<Summary>("/api/dashboard/summary");
      return data;
    },
    enabled: queryEnabled,
  });

  const { data: revenue } = useQuery({
    queryKey: ["dashboard", "revenue", revPeriod],
    queryFn: async () => {
      const { data } = await api.get<{ data: { date: string; sales: number; profit: number }[] }>(
        "/api/dashboard/revenue-chart",
        { params: { period: revPeriod } },
      );
      return data.data;
    },
    enabled: queryEnabled,
  });

  const { data: topProducts } = useQuery({
    queryKey: ["dashboard", "top-products"],
    queryFn: async () => {
      const { data } = await api.get<{ products: { product_name: string; qty: number; amount: number }[] }>(
        "/api/dashboard/top-products",
        { params: { limit: 10 } },
      );
      return data.products;
    },
    enabled: queryEnabled,
  });

  const { data: heatmap } = useQuery({
    queryKey: ["dashboard", "heatmap"],
    queryFn: async () => {
      const { data } = await api.get<{
        matrix: unknown;
        max_amount: number;
      }>("/api/dashboard/hourly-heatmap");
      return data;
    },
    enabled: queryEnabled,
  });

  const { data: payMix } = useQuery({
    queryKey: ["dashboard", "payment-mix"],
    queryFn: async () => {
      const { data } = await api.get<{ mix: { mode: string; amount: number }[] }>(
        "/api/dashboard/payment-mix",
        { params: { range: "today" } },
      );
      return data.mix;
    },
    enabled: queryEnabled,
  });

  const { data: lowStock } = useQuery({
    queryKey: ["dashboard", "low-stock"],
    queryFn: async () => {
      const { data } = await api.get<{ items: { id: number; name: string; stock_qty: number }[] }>(
        "/api/dashboard/low-stock",
        { params: { limit: 12 } },
      );
      return data.items;
    },
    enabled: queryEnabled,
  });

  const matrix = useMemo(() => normalizeMatrix(heatmap?.matrix), [heatmap?.matrix]);
  const pieData = useMemo(
    () => (payMix ?? []).filter((p) => p.amount > 0).map((p) => ({ name: p.mode, value: p.amount })),
    [payMix],
  );
  const pieTotal = useMemo(() => pieData.reduce((sum, p) => sum + p.value, 0), [pieData]);

  const barData = useMemo(
    () =>
      (topProducts ?? []).map((p) => ({
        name: p.product_name.length > 18 ? p.product_name.slice(0, 18) + "…" : p.product_name,
        amount: Number(p.amount),
      })),
    [topProducts],
  );

  if (!accessToken) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Loading session…</div>
    );
  }

  if (summaryQ.isPending) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Loading dashboard…</div>
    );
  }

  if (summaryQ.isError) {
    const err = summaryQ.error as { response?: { data?: { error?: string } } } | undefined;
    return (
      <div className="p-6 text-sm text-destructive">
        {err?.response?.data?.error ?? "Failed to load dashboard."}
      </div>
    );
  }

  const summary = summaryQ.data;
  if (!summary) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Loading dashboard…</div>
    );
  }

  const userName = me?.name ?? "there";
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <div className="min-h-full space-y-6 bg-background p-6">
      {/* Hero */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
              <LayoutDashboard className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-primary">
                {greeting}
              </p>
              <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                {userName}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Sales, profit, and operations at a glance.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/60 px-3 py-1.5 text-xs font-medium text-foreground">
              <CalendarDays className="h-3.5 w-3.5" />
              {formatToday()}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/60 px-3 py-1.5 text-xs font-semibold text-foreground">
              <Sparkles className="h-3.5 w-3.5" />
              Month: {money.format(summary.this_month.sales_amount)} ·{" "}
              {summary.this_month.bills_count} bills
            </span>
          </div>
        </div>

        {summary.alerts.low_stock_count + summary.alerts.expiry_soon_count + summary.alerts.pending_pos_count > 0 ? (
          <div className="mt-5 flex flex-wrap gap-2">
            {summary.alerts.low_stock_count > 0 ? (
              <AlertChip icon={PackageMinus} label={`${summary.alerts.low_stock_count} low stock`} />
            ) : null}
            {summary.alerts.expiry_soon_count > 0 ? (
              <AlertChip icon={Clock} label={`${summary.alerts.expiry_soon_count} expiring ≤30d`} />
            ) : null}
            {summary.alerts.pending_pos_count > 0 ? (
              <AlertChip icon={ShoppingBag} label={`${summary.alerts.pending_pos_count} pending PO`} />
            ) : null}
          </div>
        ) : null}
      </div>

      {/* KPI cards */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          icon={TrendingUp}
          title="Today sales"
          value={money.format(summary.today.sales_amount)}
          hint={`Avg bill ${money.format(summary.today.avg_bill)}`}
        />
        <MetricCard icon={Receipt} title="Bills today" value={String(summary.today.bills_count)} />
        <MetricCard icon={Wallet} title="Gross profit (est.)" value={money.format(summary.today.profit)} />
        <MetricCard
          icon={PiggyBank}
          title="Outstanding"
          hint={`Credit ${moneyDecimal.format(summary.outstanding.customer_credit)} · Payable ${moneyDecimal.format(summary.outstanding.supplier_payable)}`}
          value={money.format(summary.outstanding.customer_credit + summary.outstanding.supplier_payable)}
        />
      </div>

      {/* Revenue + payments */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="gap-0 overflow-hidden border-border bg-card py-0 shadow-sm lg:col-span-2">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/50 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <TrendingUp className="h-4 w-4" />
              </div>
              <CardTitle className="text-base text-foreground">Revenue &amp; profit</CardTitle>
            </div>
            <div className="flex gap-1">
              {(["7d", "30d", "90d"] as const).map((p) => (
                <Button
                  key={p}
                  type="button"
                  variant={revPeriod === p ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setRevPeriod(p)}
                >
                  {p}
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="h-[280px] pt-4">
            <ResponsiveContainer
              key={isDarkTheme ? "revenue-dark" : "revenue-light"}
              width="100%"
              height="100%"
            >
              <AreaChart data={revenue ?? []} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0.04} />
                  </linearGradient>
                  <linearGradient id="profitFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0369a1" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0369a1" stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: theme.tick }} />
                <YAxis tick={{ fontSize: 11, fill: theme.tick }} tickFormatter={(v) => `₹${v}`} />
                <Tooltip formatter={(v: number) => money.format(v)} {...tooltipProps} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="sales"
                  name="Sales"
                  stroke="#0ea5e9"
                  strokeWidth={2.5}
                  fill="url(#salesFill)"
                />
                <Area
                  type="monotone"
                  dataKey="profit"
                  name="Profit"
                  stroke="#0369a1"
                  strokeWidth={2.5}
                  fill="url(#profitFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="gap-0 overflow-hidden border-border bg-card py-0 shadow-sm">
          <CardHeader className="border-b border-border bg-muted/50 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <Wallet className="h-4 w-4" />
              </div>
              <CardTitle className="text-base text-foreground">Payments (today)</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="relative h-[280px] pt-4">
            {pieData.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-sm text-muted-foreground">
                <Wallet className="mb-2 h-6 w-6 opacity-40" />
                No payment split yet.
              </div>
            ) : (
              <>
                <ResponsiveContainer
                  key={isDarkTheme ? "pie-dark" : "pie-light"}
                  width="100%"
                  height="100%"
                >
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={2}
                      stroke={theme.pieStroke}
                      strokeWidth={2}
                    >
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={SKY_PALETTE[i % SKY_PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => money.format(v)} {...tooltipProps} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[60%] text-center">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Total
                  </p>
                  <p className="text-base font-semibold tabular-nums text-foreground">
                    {money.format(pieTotal)}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top products + low stock */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="gap-0 overflow-hidden border-border bg-card py-0 shadow-sm">
          <CardHeader className="border-b border-border bg-muted/50 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <ShoppingBag className="h-4 w-4" />
              </div>
              <CardTitle className="text-base text-foreground">Top products (90d)</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="h-[300px] pt-4">
            <ResponsiveContainer
              key={isDarkTheme ? "top-dark" : "top-light"}
              width="100%"
              height="100%"
            >
              <BarChart data={barData} layout="vertical" margin={{ left: 8, right: 8 }}>
                <defs>
                  <linearGradient id="topBar" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#38bdf8" />
                    <stop offset="100%" stopColor="#0369a1" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={theme.grid} />
                <XAxis type="number" tick={{ fontSize: 11, fill: theme.tick }} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10, fill: theme.tick }} />
                <Tooltip cursor={false} formatter={(v: number) => money.format(v)} {...tooltipProps} />
                <Bar dataKey="amount" fill="url(#topBar)" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="gap-0 overflow-hidden border-border bg-card py-0 shadow-sm">
          <CardHeader className="border-b border-border bg-muted/50 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <PackageMinus className="h-4 w-4" />
              </div>
              <CardTitle className="text-base text-foreground">Low stock</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <ul className="max-h-[280px] space-y-2 overflow-y-auto pr-1 text-sm">
              {(lowStock ?? []).map((p) => {
                const qty = Number(p.stock_qty);
                const critical = qty <= 0;

                return (
                  <li
                    key={p.id}
                    className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      {critical ? (
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                      ) : (
                        <PackageMinus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      )}
                      <span className="truncate font-medium text-foreground">{p.name}</span>
                    </div>
                    <span
                      className={cn(
                        "shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold tabular-nums text-foreground",
                        critical && "bg-destructive/20 text-destructive",
                      )}
                    >
                      {qty}
                    </span>
                  </li>
                );
              })}
              {(lowStock ?? []).length === 0 && (
                <li className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center text-muted-foreground">
                  No low-stock SKUs.
                </li>
              )}
            </ul>
            <p className="mt-3 flex flex-wrap gap-1.5 text-xs">
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-foreground">
                low {summary.alerts.low_stock_count}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-foreground">
                expiry ≤30d {summary.alerts.expiry_soon_count}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-foreground">
                pending PO {summary.alerts.pending_pos_count}
              </span>
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="gap-0 overflow-hidden border-border bg-card py-0 shadow-sm">
        <CardHeader className="border-b border-border bg-muted/50 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary">
              <Clock className="h-4 w-4" />
            </div>
            <CardTitle className="text-base text-foreground">Hourly heatmap (last 90d, Mon–Sun)</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <HourlyHeatmap
            matrix={matrix}
            maxAmount={heatmap?.max_amount ?? 1}
            darkMode={isDarkTheme}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({
  title,
  value,
  hint,
  icon: Icon,
}: {
  title: string;
  value: string;
  hint?: string;
  icon: LucideIcon;
}) {
  return (
    <Card className="border-border bg-card shadow-sm transition-shadow hover:shadow-md">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </CardTitle>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      <CardContent>
        <p className="font-mono text-2xl font-bold tabular-nums tracking-tight text-foreground">{value}</p>
        {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

function AlertChip({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/60 px-3 py-1 text-xs font-medium text-foreground">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}
